import {useState} from 'react';
import {creates, hoistCmp, HoistProps, XH} from '@xh/hoist/core';
import {useOnMount} from '@xh/hoist/utils/react';
import {TickerPanelModel} from './TickerPanelModel';
import {div, filler, fragment, hbox, hspacer, span, strong, vbox} from '@xh/hoist/cmp/layout';
import {button} from '@xh/hoist/desktop/cmp/button';
import './TickerPanel.scss';
import {orderTicketDialog} from '../orders/OrderTicket';
import {popover} from '@xh/hoist/kit/blueprint';
import {Position} from '../../../../data/DataTypes';
import {Icon} from '@xh/hoist/icon';
import {fmtNumber, fmtPercent, fmtPrice} from '@xh/hoist/format';
import {secExchangeBadge} from '../../../../cmp/secBadges/SecBadges';
import {fileChartPieIcon} from '../../../../../core/Icons';

interface TickerPopupProps extends HoistProps<TickerPanelModel> {
    position: Position;
    /** Optional live lookup of the position, read on open - for grid cells that are not re-rendered on each update. */
    getPosition?: () => Position;
}

export const tickerPopup = hoistCmp.factory<TickerPopupProps>({
    model: creates(TickerPanelModel),
    render: ({model}) => {
        // Grid autosize measures cells with renderToStaticMarkup, where the popover's layout effects only raise
        // warnings. Effects never run there, so render the popover only once mounted in the browser.
        const [mounted, setMounted] = useState(false);
        useOnMount(() => setMounted(true));

        return fragment(
            strong(model.position.ticker),
            // Target is an empty overlay stretched over the whole grid cell (see scss), so the popup opens on hover
            // anywhere in the cell and anchors to the cell's right edge rather than to the ticker text.
            popover({
                omit: !mounted,
                className: 'ticker-popup__target',
                item: span(),
                placement: 'right',
                lazy: true,
                interactionKind: 'hover',
                onInteraction: state => {
                    if (state) model.open();
                    else model.close();
                },
                isOpen: model.isOpen,
                hoverOpenDelay: 50,
                hoverCloseDelay: 50,
                content: tickerPanel()
            }),
            orderTicketDialog({model: model.orderModel})
        );
    }
});

const tickerPanel = hoistCmp.factory<TickerPanelModel>(({model}) => {
    const className = 'ticker-panel';
    return hbox({
        className,
        items: [actionButtons(), vbox(name(), description(), price(), priceDetails({omit: !model.isListed}))]
    });
});

const actionButtons = hoistCmp.factory<TickerPanelModel>(({model}) =>
    vbox({
        className: 'ticker-panel__order-buttons',
        items: [
            // A shadow user is read-only: no orders or deposits
            button({
                omit: XH.entitlementService.isShadow,
                text: model.isCash ? 'DEPOSIT' : 'BUY',
                intent: 'success',
                minimal: false,
                onClick: () => model.buy()
            }),
            button({
                omit: XH.entitlementService.isShadow,
                text: model.isCash ? 'WITHDRAW' : 'SELL',
                intent: 'danger',
                minimal: false,
                onClick: () => model.sell()
            }),
            hbox({
                omit: !model.isListed,
                items: [
                    button({
                        icon: Icon.chartLine(),
                        tooltip: 'Send to Charts',
                        outlined: true,
                        intent: 'primary',
                        className: 'ticker-panel__action-button',
                        onClick: () => {
                            model.sendToChart();
                        }
                    }),
                    hspacer(5),
                    button({
                        icon: fileChartPieIcon(),
                        tooltip: 'Send to Research',
                        outlined: true,
                        intent: 'primary',
                        className: 'ticker-panel__action-button',
                        onClick: () => {
                            model.sendToResearch();
                        }
                    })
                ]
            })
        ]
    })
);

const name = hoistCmp.factory<TickerPanelModel>(({model}) => span({className: 'ticker-panel__name', item: model.name}));

const description = hoistCmp.factory<TickerPanelModel>(({model}) => span({className: 'ticker-panel__description', item: model.description}));

const price = hoistCmp.factory<TickerPanelModel>(({model}) =>
    hbox({
        alignItems: 'baseline',
        items: [
            span({
                item: fmtPrice(model.price, {prefix: '$'}),
                className: `ticker-panel__price`
            }),
            div({
                items: [
                    fmtNumber(model.priceChange, {
                        precision: 2
                    }),
                    ' (',
                    fmtPercent(model.priceChangePct),
                    ')'
                ],
                className: `ticker-panel__change ${model.priceChange < 0 ? 'neg' : 'pos'}`
            }),
            filler(),
            model.isListed ? secExchangeBadge(model.exchange) : null
        ]
    })
);

const priceDetails = hoistCmp.factory<TickerPanelModel>(({model}) =>
    hbox(
        span({className: 'ticker-panel__label', item: 'bid:'}),
        hspacer(5),
        span({className: 'ticker-panle__size', item: model.bidSize}),
        ' x',
        span({
            className: 'ticker-panel__bidask',
            item: fmtNumber(model.bid, {precision: 2})
        }),
        filler(),
        span({className: 'ticker-panel__label', item: 'ask:'}),
        hspacer(5),
        span({className: 'ticker-panle__size', item: model.askSize}),
        ' x',
        span({
            className: 'ticker-panel__bidask',
            item: fmtNumber(model.ask, {precision: 2})
        })
    )
);
